import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Check as CheckIcon } from '@mui/icons-material'
import DialogShell from '../common/DialogShell'
import { jobOpportunitiesApi } from '../../services/api'
import type { ImportedJobApplication, JobApplicationImportResult } from '../../services/api'
import { APPLY_DECISION_LABELS } from '../../state/jobOpportunities'

/**
 * Row 11 — bulk backfill of a client's application history.
 *
 * Staff paste rows straight out of the Momentum Maximizer workbook's Job
 * Applications tab. Excel and Sheets put tab-separated text on the clipboard, so
 * the paste is split on tabs and mapped BY POSITION rather than by header: the
 * workbook's header spans several merged rows, and its decision column is titled
 * with the client's own first name, so no fixed header match would survive the
 * next client.
 *
 * Nothing is sent until the preview is clean. Rows carrying a status this app has
 * no equivalent for are held back for staff to resolve, never quietly reinterpreted.
 */

// The workbook's column order. Date Posted and Last Follow Up map to their own
// fields (datePosted, lastFollowUpDate). Interview Yielded still has no field on
// the record and is folded into notes.
const COLUMNS = [
  'Category',
  'Company',
  'Job Title',
  'URL',
  'Decision',
  'Status',
  'Location',
  'Date Posted',
  'Date Applied',
  'Last Follow Up',
  'Interview Yielded',
]

type AppStatus = ImportedJobApplication['status']

// Adopted verbatim from the client's own Momentum Maximizer Status dropdown
// (every status; the one state a pasted row could not be in — awaiting the
// client's decision — is no longer a status at all, see IMPORTABLE_STATUSES
// in backend/layers/helpers/nodejs/jobOpportunityRules.mjs). Every status here
// imports, including the two the Add dialog cannot set: a pasted row is the
// client's real history, so the import creates the interview behind an
// "Interviewing"/"Got the Job" row rather than holding the row back.
const STATUS_LABELS: Record<AppStatus, string> = {
  applied: 'Applied',
  following_up: 'Following up',
  interviewing: 'Interviewing',
  got_the_job: 'Got the Job',
  rejected: 'Rejected',
  closed_not_accepting: 'Closed/Not Accepting Anymore',
}

// Observed in the real workbooks, plus the obvious near-misses. Anything not here
// is surfaced for a human to map — never guessed.
function mapStatus(raw: string): { status?: AppStatus; closeReason?: string } {
  const v = raw.trim().toLowerCase()
  if (!v) return {}
  if (v === 'applied') return { status: 'applied' }
  if (v === 'following up' || v === 'following-up' || v === 'follow up' || v === 'followup') {
    return { status: 'following_up' }
  }
  if (v === 'interviewing' || v === 'interview') return { status: 'interviewing' }
  if (v === 'got the job' || v === 'offer' || v === 'offer received') return { status: 'got_the_job' }
  if (v === 'rejected') return { status: 'rejected' }
  if (v === 'withdrew' || v === 'withdrawn') return { status: 'closed_not_accepting', closeReason: 'withdrew' }
  if (v === 'no response' || v === 'ghosted') return { status: 'closed_not_accepting', closeReason: 'no_response' }
  if (v === 'position filled' || v === 'filled') return { status: 'closed_not_accepting', closeReason: 'other' }
  if (v === 'closed' || v === 'closed/not accepting anymore' || v === 'not accepting anymore') {
    return { status: 'closed_not_accepting', closeReason: 'other' }
  }
  return {}
}

// The client's own DECISION dropdown, verbatim: Apply on my behalf, I will apply,
// Not Interested, Not Suitable. 'self' is kept alongside 'i will apply' for
// workbooks pasted before the client's relabel.
function mapDecision(raw: string): ImportedJobApplication['applyDecision'] | undefined {
  const v = raw.trim().toLowerCase()
  if (v.includes('on my behalf') || v.includes('on their behalf')) return 'apply_on_behalf'
  if (v.includes('not suitable')) return 'not_suitable'
  if (v.includes('not interested')) return 'not_interested'
  if (v.includes('i will apply') || v.includes('self')) return 'self_apply'
  return undefined
}

interface ParsedRow {
  rowNumber: number
  company: string
  jobTitle: string
  status?: AppStatus
  closeReason?: string
  rawStatus: string
  applicationDate: string
  datePosted: string
  location: string
  url: string
  notes: string
  lastFollowUpDate: string
  applyDecision?: ImportedJobApplication['applyDecision']
  rawApplyDecision: string
  invalid?: string
}

function parsePaste(text: string): ParsedRow[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0)
  if (lines.length === 0) return []

  // Drop a header row if they pasted one.
  const first = lines[0].toLowerCase()
  const body = /company name|job posting|category/.test(first) ? lines.slice(1) : lines

  return body.map((line, i) => {
    const cells = line.split('\t').map((c) => c.trim().replace(/^"|"$/g, ''))
    const at = (n: number) => cells[n] ?? ''
    const rawStatus = at(5)
    const { status, closeReason } = mapStatus(rawStatus)
    // at(0) is the workbook's Category column — no longer a field on the
    // record (tier is derived server-side from the Dream Job target
    // companies list), so a pasted value there is ignored, not parsed.
    const company = at(1)
    const jobTitle = at(2)
    const rawApplyDecision = at(4)

    // Interview Yielded still has no field on the record — keep it rather than
    // dropping it on the floor.
    const extras = [at(10) ? `Interview yielded: ${at(10)}` : ''].filter(Boolean)

    return {
      rowNumber: i + 1,
      company,
      jobTitle,
      status,
      closeReason,
      rawStatus,
      applicationDate: at(8),
      datePosted: at(7),
      location: at(6),
      url: at(3),
      notes: extras.join('\n'),
      lastFollowUpDate: at(9),
      applyDecision: mapDecision(rawApplyDecision),
      rawApplyDecision,
      invalid: !company || !jobTitle ? 'Company and job title are both required' : undefined,
    }
  })
}

const STEP_LABELS = ['Paste', 'Preview', 'Result']

// Makes the three existing stages visible; it adds none.
function ImportSteps({ current }: { current: number }) {
  return (
    <Box
      component="ol"
      aria-label="Import progress"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, listStyle: 'none', m: 0, p: 0 }}
    >
      {STEP_LABELS.map((label, i) => {
        const n = i + 1
        const done = n < current
        const on = n === current
        return (
          <Box
            component="li"
            key={label}
            aria-current={on ? 'step' : undefined}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
          >
            {i > 0 && <Box aria-hidden sx={{ width: 16, height: '1px', bgcolor: 'divider' }} />}
            <Box
              component="span"
              sx={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: 1,
                borderColor: done ? 'success.dark' : on ? 'text.primary' : 'divider',
                bgcolor: done ? 'success.dark' : on ? 'text.primary' : 'transparent',
                color: done || on ? 'background.paper' : 'text.secondary',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                typography: 'badge',
              }}
            >
              {done ? <CheckIcon aria-hidden sx={{ fontSize: 12 }} /> : n}
            </Box>
            <Typography variant="caption" sx={{ color: on ? 'text.primary' : 'text.secondary' }}>
              {label}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}

// Below this container width the preview's seven columns cannot share a row, so
// each row turns into a labelled card instead of scrolling sideways. The
// breakpoint is on the dialog's own width, not the viewport: the dialog is
// narrower than the screen at every size.
const STACK_BELOW_PX = 620

const stackedTableSx = {
  [`@container (max-width: ${STACK_BELOW_PX}px)`]: {
    maxHeight: 'none',
    overflowX: 'visible',
    '& table, & tbody': { display: 'block' },
    '& thead': { display: 'none' },
    '& tbody tr': {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      columnGap: 1.5,
      rowGap: 0.75,
      p: 1.5,
      mb: 1,
      border: 1,
      borderColor: 'divider',
      borderRadius: '10px',
    },
    '& tbody td': { display: 'block', border: 0, p: 0, minWidth: 0, overflowWrap: 'anywhere' },
    '& .stack-label': { display: 'block' },
    '& .span-all': { gridColumn: '1 / -1' },
    '& .row-cell': { fontWeight: 600 },
    '& .row-cell .stack-label': { display: 'inline', mr: 0.5 },
    '& .MuiChip-root': { height: 'auto', maxWidth: '100%' },
    '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
    '& .MuiInputBase-root': { width: '100%' },
  },
}

// A table cell that carries its own column label, shown only once the row has
// become a card and the header row is hidden.
function StackCell({
  label,
  children,
  first,
  spanAll,
  numeric,
}: {
  label: string
  children: React.ReactNode
  first?: boolean
  spanAll?: boolean
  numeric?: boolean
}) {
  return (
    <TableCell
      className={[first && 'row-cell span-all', spanAll && 'span-all'].filter(Boolean).join(' ') || undefined}
      sx={numeric ? { fontVariantNumeric: 'tabular-nums' } : undefined}
    >
      <Box component="span" className="stack-label" sx={{ display: 'none', typography: 'badge', color: 'text.secondary' }}>
        {label}
      </Box>
      {children}
    </TableCell>
  )
}

interface ImportJobApplicationsDialogProps {
  open: boolean
  onClose: () => void
  clientId: string
  clientName?: string
  onImported: () => void
}

export default function ImportJobApplicationsDialog({
  open,
  onClose,
  clientId,
  clientName,
  onImported,
}: ImportJobApplicationsDialogProps) {
  const [raw, setRaw] = useState('')
  const [rows, setRows] = useState<ParsedRow[] | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<JobApplicationImportResult | null>(null)

  const counts = useMemo(() => {
    if (!rows) return { total: 0, ready: 0, needStatus: 0, invalid: 0, decisionUnmatched: 0 }
    return {
      total: rows.length,
      ready: rows.filter((r) => r.status && !r.invalid).length,
      needStatus: rows.filter((r) => !r.status && !r.invalid).length,
      invalid: rows.filter((r) => r.invalid).length,
      // A decision was pasted but doesn't match any of the client's four DECISION
      // labels — never silently dropped, always shown to staff (see the Decision
      // column and mapDecision above).
      decisionUnmatched: rows.filter((r) => r.rawApplyDecision && !r.applyDecision).length,
    }
  }, [rows])

  const canImport = !!rows && counts.ready > 0 && counts.needStatus === 0

  const handleClose = () => {
    setRaw('')
    setRows(null)
    setError(null)
    setResult(null)
    onClose()
  }

  const handleParse = () => {
    const parsed = parsePaste(raw)
    if (parsed.length === 0) {
      setError('No rows found. Copy the data rows from the Job Applications tab and paste them here.')
      return
    }
    setError(null)
    setRows(parsed)
  }

  const setRowStatus = (rowNumber: number, status: AppStatus) => {
    setRows((prev) =>
      (prev || []).map((r) =>
        r.rowNumber === rowNumber
          ? { ...r, status, closeReason: status === 'closed_not_accepting' ? r.closeReason || 'other' : undefined }
          : r
      )
    )
  }

  const handleImport = async () => {
    if (!rows) return
    setSubmitting(true)
    setError(null)
    try {
      const applications: ImportedJobApplication[] = rows
        .filter((r): r is ParsedRow & { status: AppStatus } => !!r.status && !r.invalid)
        .map((r) => ({
          company: r.company,
          jobTitle: r.jobTitle,
          status: r.status,
          closeReason: r.closeReason,
          applicationDate: r.applicationDate,
          datePosted: r.datePosted,
          location: r.location,
          url: r.url,
          notes: r.notes,
          lastFollowUpDate: r.lastFollowUpDate,
          applyDecision: r.applyDecision,
        }))
      const res = await jobOpportunitiesApi.import({ clientId, clientName, applications })
      setResult(res)
      onImported()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed')
    } finally {
      setSubmitting(false)
    }
  }

  const step = result ? 3 : rows ? 2 : 1

  return (
    <DialogShell
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      title="Import application history"
      extra={<ImportSteps current={step} />}
      extraOwnRowOnPhone
      footer={
        result ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            {!rows ? (
              <Button onClick={handleParse} variant="contained" disabled={!raw.trim()}>
                Preview
              </Button>
            ) : (
              <>
                <Button onClick={() => setRows(null)} disabled={submitting}>
                  Back
                </Button>
                <Button onClick={handleImport} variant="contained" disabled={!canImport || submitting}>
                  {submitting ? 'Importing…' : `Import ${counts.ready}`}
                </Button>
              </>
            )}
          </>
        )
      }
    >
      {error && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result ? (
        <Stack spacing={2}>
          <Alert severity="success" variant="outlined">
            Imported {result.imported} application{result.imported === 1 ? '' : 's'}
            {result.skippedCount > 0 ? `, skipped ${result.skippedCount}.` : '.'}
          </Alert>
          {result.skipped.length > 0 && (
            <Box>
              <Typography variant="subtitle2" component="h3" gutterBottom>
                Skipped rows
              </Typography>
              <Box sx={{ containerType: 'inline-size' }}>
                <TableContainer sx={{ maxHeight: 320, overflowX: 'auto', ...stackedTableSx }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Job title</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.skipped.map((s) => (
                        <TableRow key={s.rowNumber}>
                          <StackCell label="Row" first numeric>
                            {s.rowNumber}
                          </StackCell>
                          <StackCell label="Company">{s.company}</StackCell>
                          <StackCell label="Job title">{s.jobTitle}</StackCell>
                          <StackCell label="Reason" spanAll>
                            {s.reason}
                          </StackCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </Stack>
      ) : !rows ? (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '72ch' }}>
            Select the data rows on the Job Applications tab of the client&apos;s Momentum Maximizer
            and paste them below. A header row is fine — it will be ignored. Columns are read in the
            workbook&apos;s order:
          </Typography>
          <Box
            component="ol"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, listStyle: 'none', m: 0, p: 0, mb: 2 }}
          >
            {COLUMNS.map((c, i) => (
              <li key={c}>
                <Chip
                  variant="outlined"
                  size="small"
                  label={
                    <>
                      <Box component="span" sx={{ color: 'text.secondary', mr: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                        {i + 1}
                      </Box>
                      {c}
                    </>
                  }
                />
              </li>
            ))}
          </Box>
          <TextField
            label="Pasted rows"
            multiline
            minRows={10}
            fullWidth
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="Paste here"
          />
        </Box>
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${counts.total} rows`} size="small" variant="outlined" />
            <Chip label={`${counts.ready} ready`} color="success" size="small" variant="outlined" />
            {counts.needStatus > 0 && (
              <Chip label={`${counts.needStatus} need a status`} color="warning" size="small" variant="outlined" />
            )}
            {counts.invalid > 0 && (
              <Chip label={`${counts.invalid} unusable`} color="error" size="small" variant="outlined" />
            )}
            {counts.decisionUnmatched > 0 && (
              <Chip
                label={`${counts.decisionUnmatched} decision not recognized`}
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
          {counts.needStatus > 0 && (
            <Alert severity="warning" variant="outlined">
              Some rows use a status this app does not have. Pick one for each before importing.
            </Alert>
          )}
          {counts.decisionUnmatched > 0 && (
            <Alert severity="warning" variant="outlined">
              Some rows have a decision this app does not recognize. Those rows will import without a
              decision — see the Decision column below.
            </Alert>
          )}
          <Box sx={{ containerType: 'inline-size' }}>
            <TableContainer sx={{ maxHeight: 420, overflowX: 'auto', ...stackedTableSx }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Job title</TableCell>
                    <TableCell>Applied</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Decision</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.rowNumber} hover>
                      <StackCell label="Row" first numeric>
                        {r.rowNumber}
                      </StackCell>
                      <StackCell label="Company">{r.company || <em>missing</em>}</StackCell>
                      <StackCell label="Job title">{r.jobTitle || <em>missing</em>}</StackCell>
                      <StackCell label="Applied" numeric>
                        {r.applicationDate || '—'}
                      </StackCell>
                      <StackCell label="Location">{r.location || '—'}</StackCell>
                      <StackCell label="Status" spanAll>
                        {r.invalid ? (
                          <Chip label={r.invalid} color="error" size="small" variant="outlined" />
                        ) : r.status ? (
                          <Chip label={STATUS_LABELS[r.status]} size="small" variant="outlined" />
                        ) : (
                          <Select
                            size="small"
                            value=""
                            displayEmpty
                            onChange={(e) => setRowStatus(r.rowNumber, e.target.value as AppStatus)}
                            sx={{ minWidth: 180 }}
                            inputProps={{ 'aria-label': `Status for row ${r.rowNumber}` }}
                            renderValue={() => `"${r.rawStatus || 'blank'}" — pick one`}
                          >
                            {(Object.keys(STATUS_LABELS) as AppStatus[]).map((s) => (
                              <MenuItem key={s} value={s}>
                                {STATUS_LABELS[s]}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </StackCell>
                      <StackCell label="Decision" spanAll>
                        {r.applyDecision ? (
                          APPLY_DECISION_LABELS[r.applyDecision] || r.applyDecision
                        ) : r.rawApplyDecision ? (
                          <Chip
                            label={`"${r.rawApplyDecision}" not recognized`}
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          '—'
                        )}
                      </StackCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      )}
    </DialogShell>
  )
}
